import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import Header from "../components/Header";
import { toast } from "sonner";
import { Boat, Warning } from "@phosphor-icons/react";

function toIsoLocal(dateStr, timeStr) {
    // dateStr: yyyy-mm-dd, timeStr: HH:mm
    if (!dateStr || !timeStr) return null;
    const d = new Date(`${dateStr}T${timeStr}:00`);
    return d.toISOString();
}

export default function NewVessel() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        client_name: "",
        vessel_name: "",
        tonnage: "",
        product: "COAL",
        customer: "",
        division: "",
        start_date: "",
        start_time: "",
    });
    const [loading, setLoading] = useState(false);
    const [activeVessel, setActiveVessel] = useState(null);
    const [checking, setChecking] = useState(true);
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/vessels", { params: { status: "active" } });
                if (data.length > 0) setActiveVessel(data[0]);
            } finally {
                setChecking(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const start_datetime = toIsoLocal(form.start_date, form.start_time);
            if (!start_datetime) throw new Error("Please enter start date and time");
            if (!form.division) throw new Error("Please select a division (GTRB or RBTG)");
            const payload = {
                client_name: form.client_name,
                vessel_name: form.vessel_name,
                tonnage: parseFloat(form.tonnage),
                product: form.product,
                customer: form.customer || null,
                division: form.division,
                start_datetime,
            };
            const { data } = await api.post("/vessels", payload);
            toast.success(`${data.vessel_name} is now tracked live`);
            nav(`/vessel/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen">
            <Header />
            <main className="max-w-3xl mx-auto px-5 py-10">
                <div className="mb-8">
                    <div className="uppercase-label text-[var(--grindrod-red)]">New operation</div>
                    <h1 className="font-display font-black text-3xl md:text-4xl text-[var(--grindrod-navy)]">
                        Start a new vessel load
                    </h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Fill in the vessel details. 2-hourly reporting starts from the start time.
                    </p>
                </div>

                {checking ? (
                    <div className="text-slate-500" data-testid="new-vessel-checking">Checking current operations…</div>
                ) : activeVessel ? (
                    <div className="g-card p-8 text-center" data-testid="new-vessel-blocked">
                        <Warning size={48} weight="duotone" className="mx-auto text-[var(--grindrod-red)] mb-3" />
                        <div className="font-display font-black text-2xl text-[var(--grindrod-navy)]">
                            Vessel already in progress
                        </div>
                        <p className="text-slate-600 text-sm mt-2 max-w-md mx-auto">
                            <span className="font-semibold text-[var(--grindrod-navy)]">{activeVessel.vessel_name}</span>{" "}
                            for {activeVessel.client_name} is still loading. Complete it before starting a new vessel.
                        </p>
                        <div className="mt-6 flex justify-center gap-3">
                            <button onClick={() => nav(`/vessel/${activeVessel.id}`)} className="btn-primary" data-testid="go-active-vessel-btn">
                                Go to current vessel
                            </button>
                            <button onClick={() => nav("/dashboard")} className="btn-outline">
                                Back to dashboard
                            </button>
                        </div>
                    </div>
                ) : (
                <form onSubmit={submit} className="g-card p-6 space-y-5" data-testid="new-vessel-form">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-100">
                        <Boat size={20} className="text-[var(--grindrod-navy)]" />
                        <div className="font-display font-bold text-[var(--grindrod-navy)]">Vessel Information</div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="uppercase-label block mb-1.5">Client Name</label>
                            <input
                                className="g-input"
                                value={form.client_name}
                                onChange={set("client_name")}
                                required
                                data-testid="new-vessel-client-input"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Vessel Name</label>
                            <input
                                className="g-input"
                                value={form.vessel_name}
                                onChange={set("vessel_name")}
                                required
                                placeholder="MV JAHAN BROTHER I"
                                data-testid="new-vessel-name-input"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Tonnage (tons)</label>
                            <input
                                type="number"
                                step="1"
                                min="1"
                                className="g-input tabular"
                                value={form.tonnage}
                                onChange={set("tonnage")}
                                required
                                placeholder="52000"
                                data-testid="new-vessel-tonnage-input"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Product</label>
                            <input
                                className="g-input"
                                value={form.product}
                                onChange={set("product")}
                                placeholder="COAL"
                                data-testid="new-vessel-product-input"
                            />
                        </div>
                        <div>
                            <label className="uppercase-label block mb-1.5">Customer</label>
                            <input
                                className="g-input"
                                value={form.customer}
                                onChange={set("customer")}
                                placeholder="e.g. exxaro coal"
                                data-testid="new-vessel-customer-input"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="uppercase-label block mb-1.5">Division <span className="text-[var(--grindrod-red)]">*</span></label>
                            <div className="grid grid-cols-2 gap-3" data-testid="new-vessel-division-group">
                                {[
                                    { key: "GTRB", title: "GTRB", sub: "Grindrod Terminals Richards Bay" },
                                    { key: "RBTG", title: "RBTG", sub: "Richards Bay Terminals Grindrod" },
                                ].map((d) => (
                                    <button
                                        key={d.key}
                                        type="button"
                                        data-testid={`new-vessel-division-${d.key}`}
                                        onClick={() => setForm((f) => ({ ...f, division: d.key }))}
                                        className={`text-left p-3 rounded-md border-2 transition ${
                                            form.division === d.key
                                                ? "border-[var(--grindrod-navy)] bg-[var(--grindrod-navy)]/5"
                                                : "border-slate-200 hover:border-slate-300"
                                        }`}
                                    >
                                        <div className={`font-display font-black text-lg ${form.division === d.key ? "text-[var(--grindrod-navy)]" : "text-slate-700"}`}>
                                            {d.title}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">{d.sub}</div>
                                    </button>
                                ))}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1.5">
                                Determines which entity's monthly budget the vessel's costs roll up into on the Terminal Report.
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="uppercase-label block mb-1.5">Start Date</label>
                                <input
                                    type="date"
                                    className="g-input"
                                    value={form.start_date}
                                    onChange={set("start_date")}
                                    required
                                    data-testid="new-vessel-start-date-input"
                                />
                            </div>
                            <div>
                                <label className="uppercase-label block mb-1.5">Start Time</label>
                                <input
                                    type="time"
                                    className="g-input"
                                    value={form.start_time}
                                    onChange={set("start_time")}
                                    required
                                    data-testid="new-vessel-start-time-input"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-end gap-3 pt-3">
                        <button type="button" className="btn-outline" onClick={() => nav(-1)} data-testid="new-vessel-cancel">
                            Cancel
                        </button>
                        <button type="submit" className="btn-primary" disabled={loading} data-testid="new-vessel-submit">
                            {loading ? "Creating…" : "Start tracking"}
                        </button>
                    </div>
                </form>
                )}
            </main>
        </div>
    );
}
